<template>
  <b-card v-if="data" no-body class="cs">
    <div class="cs-head">
      <div>
        <div class="cs-head__t">{{ $t('csTitle') }}</div>
        <div class="cs-head__s">{{ data.calibration_note }}</div>
      </div>
      <div class="cs-stats">
        <div class="cs-stat">
          <div class="cs-stat__k">{{ $t('csCost') }} · {{ data.bucket_label }}</div>
          <div class="cs-stat__v">$ {{ data.supplier_cost | money }}</div>
        </div>
        <div class="cs-stat">
          <div class="cs-stat__k">{{ $t('ceCommission') }}</div>
          <div class="cs-stat__v cs-stat__v--brand">$ {{ liveCommission | money }}</div>
          <div class="cs-stat__pct">{{ livePct }}% {{ $t('csOfCost') }}</div>
        </div>
      </div>
    </div>

    <div class="cs-options">
      <div
        v-for="opt in data.options"
        :key="opt.level"
        class="cs-opt"
        :class="{ 'cs-opt--rec': opt.level === data.recommended, 'cs-opt--active': activeLevel === opt.level }"
        @click="applyOption(opt.level)"
      >
        <div class="cs-opt__tag">
          {{ optionLabel(opt.level) }}
          <span v-if="opt.level === data.recommended" class="cs-opt__rec">★</span>
        </div>
        <div class="cs-opt__amount">$ {{ opt.amount | money }}</div>
        <div class="cs-opt__rate">{{ opt.effective_rate_pct }}% {{ $t('csOfCost') }}</div>
      </div>
    </div>

    <div v-if="data.band" class="cs-band">
      <div class="cs-band__top">
        <span class="cs-band__k">{{ $t('csBandTitle') }}</span>
        <span class="cs-verdict" :class="`cs-verdict--${liveVerdict}`">{{ verdictLabel }}</span>
      </div>
      <input
        type="range"
        min="0"
        :max="masterMax"
        step="1"
        :value="liveCommission"
        class="cs-range cs-master"
        :style="masterStyle"
        @input="onMaster($event.target.value)"
      >
      <div class="cs-band__scale">
        <span>{{ $t('csFloor') }} <b>$ {{ data.band.floor_amount | money }}</b> · {{ data.band.floor_rate_pct }}%</span>
        <span>{{ $t('csCeiling') }} <b>$ {{ data.band.ceiling_amount | money }}</b> · {{ data.band.ceiling_rate_pct }}%</span>
      </div>
      <div class="cs-band__why">{{ data.band.ceiling_reason }}</div>
      <div v-if="overCeiling" class="cs-band__over">{{ $t('csOverCeilingNote') }}</div>
    </div>

    <div class="cs-context">
      <span v-if="data.has_savings" class="cs-context__pool">
        {{ $t('csMarketRef') }} $ {{ data.market_reference | money }} · {{ $t('csPool') }} <b>$ {{ data.value_pool | money }}</b>
      </span>
      <span v-else class="cs-context__nopool">{{ $t('csNoPool') }}</span>
      <span class="cs-context__net">
        {{ $t('csNet') }}: <b>{{ data.net_adjustment_pct >= 0 ? '+' : '' }}{{ data.net_adjustment_pct }}%</b>
      </span>
    </div>

    <div class="cs-factors">
      <div
        v-for="f in data.factors"
        :key="f.key"
        class="cs-factor"
        :class="{ 'cs-factor--off': !f.applies }"
      >
        <feather-icon
          :icon="f.applies ? 'CheckCircleIcon' : 'CircleIcon'"
          size="14"
          :class="f.applies ? (f.direction === 'up' ? 'text-danger' : 'text-success') : 'text-muted'"
        />
        <span class="cs-factor__pct" :class="f.direction === 'up' ? 'text-danger' : 'text-success'">
          {{ f.direction === 'up' ? '+' : '−' }}{{ f.pct }}%
        </span>
        <span class="cs-factor__label">{{ f.label }}</span>
        <span v-if="f.evidence" class="cs-factor__ev">{{ f.evidence }}</span>
      </div>
    </div>

    <div
      v-for="row in rows"
      :key="row.quote_item_id"
      class="cs-row"
    >
      <div class="cs-row__prod">
        <div class="cs-row__name">
          {{ row.name }}
          <span
            v-if="row.powerPct"
            class="cs-power"
            :class="row.powerPct > 0 ? 'cs-power--up' : 'cs-power--down'"
          >{{ row.powerPct > 0 ? '+' : '' }}{{ row.powerPct }}%</span>
        </div>
        <div class="cs-row__meta">{{ row.quantity }} · {{ $t('csCost') }} $ {{ row.cost | money }}</div>
        <div v-if="row.powerReason" class="cs-row__why">{{ row.powerReason }}</div>
      </div>

      <div class="cs-row__ctrl">
        <label class="cs-row__l">{{ $t('csEditWinner') }}</label>
        <b-form-select
          :value="row.selectedBranchId"
          :options="supplierOptions(row)"
          size="sm"
          @change="onSupplier(row, $event)"
        />
      </div>

      <div class="cs-row__comm">
        <label class="cs-row__l">{{ $t('csEditCommission') }}</label>
        <div class="cs-row__fields">
          <span class="cs-inp">
            <span>$</span>
            <input :value="fmt(rowTotal(row))" type="number" min="0" step="1" @input="onCommTotal(row, $event.target.value)">
          </span>
          <span class="cs-row__pct">{{ rowPct(row) }}%</span>
        </div>
        <input
          type="range"
          min="0"
          :max="rowMax(row)"
          step="1"
          :value="rowTotal(row)"
          class="cs-range"
          :style="barStyle(row)"
          @input="onCommTotal(row, $event.target.value)"
        >
      </div>

      <div class="cs-row__total">
        {{ $t('total') }}<br><b>$ {{ (row.cost + rowTotal(row)) | money }}</b>
      </div>
    </div>

    <div class="cs-foot">
      <span>{{ $t('ceCommission') }}: <b class="text-primary">$ {{ liveCommission | money }}</b> ({{ livePct }}%)</span>
      <div class="cs-foot__actions">
        <b-button variant="outline-secondary" size="sm" :disabled="applying || !canRestore" @click="restoreOriginal">
          <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
          {{ $t('csRestore') }}
        </b-button>
        <b-button variant="primary" :disabled="applying || !dirty" @click="apply">
          <b-spinner v-if="applying" small class="mr-50" />
          {{ $t('csApply') }}
        </b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  props: {
    quote: { type: Object, default: () => ({}) },
    quoteId: { type: String, default: '' },
  },

  data() {
    return {
      data: null, rows: [], applying: false, activeLevel: null, baseline: null,
    }
  },

  computed: {
    liveCommission() {
      return this.rows.reduce((a, r) => a + Number(r.commissionTotal || 0), 0)
    },
    livePct() {
      const cost = Number(this.data?.supplier_cost) || 0
      if (cost <= 0) return '0.00'
      return ((this.liveCommission / cost) * 100).toFixed(2)
    },
    dirty() {
      return this.rows.some(r => r.selectedBranchId !== r.origBranchId
        || Math.abs(Number(r.commissionTotal || 0) - r.origCommissionTotal) >= 0.01)
    },
    liveVerdict() {
      if (!this.data?.band) return 'en_banda'
      if (this.liveCommission < Number(this.data.band.floor_amount)) return 'bajo_piso'
      if (this.overCeiling) return 'sobre_techo'
      return 'en_banda'
    },
    verdictLabel() {
      if (!this.data?.band) return ''
      if (this.liveVerdict === 'bajo_piso') return this.$t('csVerdictBelow')
      if (this.liveVerdict === 'sobre_techo') return this.$t('csVerdictAbove')
      return this.$t('csVerdictInside')
    },
    overCeiling() {
      const ceiling = Number(this.data?.band?.ceiling_amount) || 0
      return ceiling > 0 && this.liveCommission > ceiling
    },
    // La barra llega al doble del techo: el admin PUEDE pasarse si logra venderlo.
    masterMax() {
      const ceiling = Number(this.data?.band?.ceiling_amount) || 0
      return Math.max(this.round2(ceiling * 2), this.round2(this.liveCommission * 1.3), 10)
    },
    masterStyle() {
      const max = this.masterMax
      const from = (Number(this.data.band.floor_amount) / max) * 100
      const to = (Number(this.data.band.ceiling_amount) / max) * 100
      return {
        background: 'linear-gradient(90deg,'
          + ` #d9f2f8 0%, #d9f2f8 ${from}%,`
          + ` rgba(40,199,111,.34) ${from}%, rgba(40,199,111,.34) ${to}%,`
          + ` rgba(234,84,85,.26) ${to}%, rgba(234,84,85,.26) 100%)`,
      }
    },
    canRestore() {
      if (!this.baseline) return false
      return this.rows.some(r => {
        const base = this.baseline[r.quote_item_id]
        if (!base) return false
        return base.branchId !== r.selectedBranchId
          || Math.abs(base.commissionTotal - Number(r.commissionTotal || 0)) >= 0.01
      })
    },
  },

  watch: {
    quote: { handler() { this.buildRows() }, immediate: true },
  },

  created() {
    if (this.quoteId) this.load()
  },

  methods: {
    load() {
      this.$store.dispatch('quotes/fetchCommissionSuggestion', this.quoteId)
        .then(res => { this.data = res; this.buildRows() })
        .catch(() => {})
    },

    dataItem(id) {
      return (this.data?.items || []).find(i => i.quote_item_id === id)
    },

    buildRows() {
      const items = (this.quote?.quote_items_attributes || [])
        .filter(i => i.active_status !== 'inactive' && i.selected_supplier_branch_id)
      this.rows = items.map(item => {
        const d = this.dataItem(item.id) || {}
        const qty = Number(item.quantity) || 0
        const currentTotal = this.round2(Number(item.unit_commission_price || 0) * qty)
        return {
          quote_item_id: item.id,
          name: item.product_catalog?.name,
          quantity: qty,
          cost: Number(d.cost) || this.round2(Number(item.unit_price || 0) * qty),
          candidates: (item.unit_prices || []).map(p => ({
            id: p.supplier_branch_id, name: p.supplier_branch_name, price: Number(p.supplier_unit_price),
          })),
          powerPct: Number(d.power_pct) || 0,
          powerReason: d.power_reason,
          floor: Number(d.floor) || 0,
          ceiling: Number(d.ceiling) || 0,
          soleSource: !!d.sole_source,
          selectedBranchId: item.selected_supplier_branch_id,
          origBranchId: item.selected_supplier_branch_id,
          unitPrice: Number(item.unit_price) || 0,
          commissionTotal: currentTotal,
          origCommissionTotal: currentTotal,
          opts: {
            conservador: Number(d.conservador) || 0,
            objetivo: Number(d.objetivo) || 0,
            agresivo: Number(d.agresivo) || 0,
          },
        }
      })
      this.captureBaseline()
    },

    // Foto de cómo llegó la cotización la primera vez que se abrió el panel.
    // No se vuelve a tomar después de aplicar: así el admin siempre puede regresar.
    captureBaseline() {
      if (this.baseline || this.rows.length === 0) return
      this.baseline = this.rows.reduce((acc, r) => {
        acc[r.quote_item_id] = {
          branchId: r.selectedBranchId,
          unitPrice: r.unitPrice,
          cost: r.cost,
          commissionTotal: r.commissionTotal,
        }
        return acc
      }, {})
    },

    restoreOriginal() {
      if (!this.baseline) return
      this.activeLevel = null
      this.rows.forEach(r => {
        const base = this.baseline[r.quote_item_id]
        if (!base) return
        r.selectedBranchId = base.branchId
        r.unitPrice = base.unitPrice
        r.cost = base.cost
        r.commissionTotal = base.commissionTotal
      })
    },

    // Mueve el total y reparte el cambio entre las partidas conservando la mezcla actual.
    onMaster(value) {
      const target = this.round2(Number(value))
      const current = this.liveCommission
      this.activeLevel = null

      if (current > 0) {
        const factor = target / current
        this.rows.forEach(r => { r.commissionTotal = this.round2(Number(r.commissionTotal || 0) * factor) })
      } else {
        const totalObj = this.rows.reduce((a, r) => a + Number(r.opts.objetivo || 0), 0)
        this.rows.forEach(r => {
          const share = totalObj > 0 ? Number(r.opts.objetivo || 0) / totalObj : 1 / this.rows.length
          r.commissionTotal = this.round2(target * share)
        })
      }
      this.absorbRounding(target)
    },

    // El redondeo por partida deja centavos sueltos; se los cargamos a la partida mayor.
    absorbRounding(target) {
      if (this.rows.length === 0) return
      const diff = this.round2(target - this.liveCommission)
      if (Math.abs(diff) < 0.01) return
      const biggest = this.rows.reduce((a, b) => (b.cost > a.cost ? b : a), this.rows[0])
      biggest.commissionTotal = Math.max(0, this.round2(biggest.commissionTotal + diff))
    },

    applyOption(level) {
      this.activeLevel = level
      this.rows.forEach(r => { r.commissionTotal = this.round2(r.opts[level] || 0) })
    },

    rowTotal(row) { return this.round2(row.commissionTotal) },
    rowPct(row) {
      if (!row.cost) return '0.00'
      return ((Number(row.commissionTotal || 0) / row.cost) * 100).toFixed(2)
    },
    // La barra deja llegar bastante arriba del techo: el admin puede pasarse, pero lo ve en rojo.
    rowMax(row) {
      return Math.max(this.round2(row.ceiling * 1.8), this.round2(row.origCommissionTotal * 1.3), 10)
    },
    barColor(row) {
      const value = Number(row.commissionTotal || 0)
      if (row.ceiling > 0 && value > row.ceiling) return '#ea5455'
      if (value < row.floor) return '#00cfe8'
      return '#28c76f'
    },
    barStyle(row) {
      const max = this.rowMax(row)
      const pct = max > 0 ? Math.min(100, (Number(row.commissionTotal || 0) / max) * 100) : 0
      return { background: `linear-gradient(90deg, ${this.barColor(row)} ${pct}%, #ebe9f1 ${pct}%)` }
    },

    onCommTotal(row, value) {
      row.commissionTotal = this.round2(Number(value))
      this.activeLevel = null
    },
    supplierOptions(row) {
      return row.candidates.map(c => ({ value: c.id, text: `${c.name} · $${this.money(c.price)}` }))
    },
    onSupplier(row, branchId) {
      const c = row.candidates.find(x => x.id === branchId)
      if (!c) return
      row.selectedBranchId = branchId
      row.unitPrice = c.price
      row.cost = this.round2(c.price * row.quantity)
    },

    async apply() {
      const ok = await this.$bvModal.msgBoxConfirm(this.$t('csApplyConfirm'), { okTitle: this.$t('csApply'), cancelTitle: this.$t('cancel'), centered: true })
      if (!ok) return
      this.applying = true
      try {
        const items = this.rows.map(r => ({
          quote_item_id: r.quote_item_id,
          supplier_branch_id: r.selectedBranchId,
          unit_price: r.unitPrice,
          unit_commission_price: r.quantity > 0 ? this.round4(Number(r.commissionTotal) / r.quantity) : 0,
        }))
        await this.$store.dispatch('quotes/applyQuoteAdjustments', { quoteId: this.quoteId, items })
        this.$emit('applied')
        this.load()
        this.$toast({ component: ToastificationContent, props: { title: this.$t('csApplied'), icon: 'CheckIcon', variant: 'success' } })
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: this.$t('csApplyError'), icon: 'AlertCircleIcon', variant: 'danger' } })
      } finally {
        this.applying = false
      }
    },

    optionLabel(level) {
      if (level === 'conservador') return this.$t('csOptConservative')
      if (level === 'agresivo') return this.$t('csOptAggressive')
      return this.$t('csOptTarget')
    },
    round2(v) { return Math.round(Number(v || 0) * 100) / 100 },
    round4(v) { return Math.round(Number(v || 0) * 1e4) / 1e4 },
    fmt(v) { return (Math.round(Number(v || 0) * 100) / 100).toString() },
    money(v) { return Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
  },
}
</script>

<style lang="scss" scoped>
.cs { --cs-line:#ebe9f1; --cs-ink:#25233a; --cs-muted:#6e6b7b; --cs-faint:#a8a5b8; overflow:hidden; }
.cs-head { display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; padding:16px 20px 14px; border-bottom:1px solid var(--cs-line);
  &__t { font-size:15px; font-weight:800; color:var(--cs-ink); }
  &__s { font-size:11.5px; color:var(--cs-muted); margin-top:2px; } }
.cs-band { padding:14px 20px; border-bottom:1px solid var(--cs-line);
  &__top { display:flex; align-items:center; justify-content:space-between; margin-bottom:9px; }
  &__k { font-size:11px; font-weight:700; color:var(--cs-faint); text-transform:uppercase; letter-spacing:.4px; }
  &__scale { display:flex; justify-content:space-between; font-size:11px; color:var(--cs-muted); margin-top:7px; }
  &__why { font-size:11px; color:var(--cs-faint); margin-top:5px; line-height:1.45; }
  &__over { font-size:11.5px; font-weight:600; color:#d14748; margin-top:5px; line-height:1.45; } }
.cs-master { height:10px; }
.cs-verdict { font-size:10.5px; font-weight:700; padding:2px 9px; border-radius:99px;
  &--en_banda { background:rgba(40,199,111,.14); color:#28a06a; }
  &--bajo_piso { background:rgba(0,207,232,.14); color:#0f9cb3; }
  &--sobre_techo { background:rgba(234,84,85,.14); color:#d14748; } }
.cs-stats { display:flex; gap:22px; }
.cs-stat { text-align:right;
  &__k { font-size:11px; color:var(--cs-faint); font-weight:600; }
  &__v { font-size:18px; font-weight:800; color:var(--cs-ink); font-variant-numeric:tabular-nums; &--brand { color:#7367f0; } }
  &__pct { font-size:11px; color:var(--cs-faint); } }

.cs-options { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; padding:14px 20px 10px; }
.cs-opt { border:1px solid var(--cs-line); border-radius:12px; padding:12px 14px; cursor:pointer; background:#fbfcfe; transition:all .15s;
  &:hover { border-color:#7367f0; }
  &--rec { background:#fff; }
  &--active { border-color:#7367f0; box-shadow:0 0 0 2px rgba(115,103,240,.25); background:#fff; }
  &__tag { font-size:10.5px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:var(--cs-faint); }
  &__rec { color:#ff9f43; }
  &__amount { font-size:20px; font-weight:800; color:var(--cs-ink); margin:2px 0; font-variant-numeric:tabular-nums; }
  &__rate { font-size:11.5px; color:var(--cs-muted); } }

.cs-context { display:flex; gap:16px; flex-wrap:wrap; padding:2px 20px 10px; font-size:12px; color:var(--cs-muted); font-variant-numeric:tabular-nums;
  &__pool b { color:#28c76f; }
  &__nopool { color:var(--cs-faint); }
  &__net { margin-left:auto; } }

.cs-factors { display:flex; flex-direction:column; gap:5px; padding:0 20px 12px; border-bottom:1px solid var(--cs-line); }
.cs-factor { display:flex; align-items:baseline; gap:8px; font-size:12px; color:var(--cs-ink); flex-wrap:wrap;
  &--off { opacity:.45; }
  &__pct { font-weight:800; width:44px; flex:none; }
  &__label { color:var(--cs-ink); font-weight:600; }
  &__ev { color:var(--cs-muted); font-size:11.5px; flex:1; min-width:180px; line-height:1.45; } }

.cs-row { display:flex; gap:16px; align-items:flex-end; padding:12px 20px; border-bottom:1px solid var(--cs-line); flex-wrap:wrap; }
.cs-row__prod { flex:1.2; min-width:150px; }
.cs-row__name { font-size:13px; font-weight:700; color:var(--cs-ink); }
.cs-row__meta { font-size:11.5px; color:var(--cs-faint); margin-top:2px; font-variant-numeric:tabular-nums; }
.cs-row__why { font-size:11px; color:var(--cs-muted); margin-top:3px; line-height:1.4; }
.cs-power { font-size:10px; font-weight:800; padding:1px 6px; border-radius:99px; margin-left:6px; vertical-align:middle;
  &--up { background:rgba(40,199,111,.14); color:#28a06a; }
  &--down { background:rgba(0,207,232,.14); color:#0f9cb3; } }
.cs-row__l { font-size:10px; font-weight:700; color:var(--cs-faint); text-transform:uppercase; letter-spacing:.05em; display:block; margin-bottom:4px; }
.cs-row__ctrl { flex:1.2; min-width:190px; }
.cs-row__comm { flex:1.2; min-width:200px; }
.cs-row__fields { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
.cs-inp { display:flex; align-items:center; gap:5px; border:1px solid var(--cs-line); border-radius:8px; padding:3px 8px; background:#fff;
  span { font-weight:800; color:var(--cs-ink); }
  input { width:80px; border:0; outline:none; font-size:14px; font-weight:800; color:var(--cs-ink); font-variant-numeric:tabular-nums; } }
.cs-row__pct { font-size:12px; font-weight:700; color:var(--cs-muted); }
.cs-range { -webkit-appearance:none; appearance:none; width:100%; height:6px; border-radius:99px; outline:none; cursor:pointer; }
.cs-range::-webkit-slider-thumb { -webkit-appearance:none; width:16px; height:16px; border-radius:50%; background:#fff; border:3px solid #7367f0; box-shadow:0 1px 4px rgba(34,41,47,.25); cursor:pointer; }
.cs-range::-moz-range-thumb { width:16px; height:16px; border-radius:50%; background:#fff; border:3px solid #7367f0; cursor:pointer; }
.cs-row__total { width:120px; text-align:right; font-size:11.5px; color:var(--cs-faint); font-variant-numeric:tabular-nums; b { font-size:14px; color:#28c76f; } }

.cs-foot { display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; padding:14px 20px; background:#fbfcfe; font-variant-numeric:tabular-nums;
  &__actions { display:flex; align-items:center; gap:10px; } }
.text-danger { color:#ea5455 !important; } .text-success { color:#28c76f !important; } .text-primary { color:#7367f0 !important; } .text-muted { color:#a8a5b8 !important; }
@media (max-width:767px) { .cs-options { grid-template-columns:1fr; } .cs-row { flex-direction:column; align-items:stretch; } .cs-row__total { width:100%; text-align:left; } }
</style>
